import { useSelector } from 'core/use-cases/utilities/';

import type { Cart } from 'core/domains';
import type { RootState } from 'core/store';
import type { SelectorReturnFn } from 'core/use-cases/utilities';

/**
 * Selects the content based on the cart and async request
 *
 */
export function useCartSelector(): SelectorReturnFn<Cart['data']> {
  const {
    data,
    fetch: { status },
    updatedAt,
  } = useSelector(({ cart }) => cart);

  return {
    data,
    status,
    updatedAt,
  };
}

/**
 * Selects the content based on the coupon and async request
 *
 */
export function useCouponSelector(): SelectorReturnFn<Cart['data']> {
  const {
    couponUpdate: { status, updatedAt = 0 },
    data,
  } = useSelector(({ cart }) => cart);

  return {
    data,
    status,
    updatedAt,
  };
}

/**
 * Selects selected shipping model data
 *
 */
export function useCartSelectedShippingModel() {
  return useSelector(({ cart }) => cart.current);
}

/**
 * Selects if cart has been called
 */
export function useCartHasBeenCalled() {
  return useSelector(({ cart }) => cart.hasBeenCalled);
}

/**
 * Selects if cart has been called
 */
export function useCartShippingModelNotFound() {
  return useSelector(({ cart }) => cart.shippingModelNotFound);
}

/**
 * Selects the info of the item to be removed from the cart
 *
 */
export function useDeleteConfirmationSelector() {
  const deleteConfirmation = useSelector(({ cart }) => cart.deleteConfirmation);

  return deleteConfirmation;
}

/**
 * Selects the info to be show under min order confirmation
 *
 */
export function useMinOrderConfirmationSelector() {
  const minOrderConfirmation = useSelector(({ cart }) => cart.minOrderConfirmation);

  return minOrderConfirmation;
}

/**
 * Selects combo edit confirmation info
 *
 */
export function useComboEditConfirmationSelector() {
  return useSelector(({ cart }) => cart.comboEditConfirmation);
}

/**
 * Selectors the value of a property through its name
 *
 */
export function useCartPropertyByKeySelector(key: keyof RootState['cart']) {
  const value = useSelector(({ cart }) => cart[key]);

  return value;
}
